import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Camera, Wifi, BookOpen, Plus, CheckCircle2, Upload, AlertTriangle,
  Loader2, ShieldCheck, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_REAL_TESTID = 'onb-photo-real';

// Map helper safe-candidate source_type to fake_homey_harness device_key
function _mapHelperSourceToFakeKey(sourceType) {
  switch (sourceType) {
    case 'homewizard_p1_direct': return 'homewizard_p1_direct';
    case 'homewizard_energy_socket': return 'homewizard_energy_socket';
    case 'homey_pro_local': return 'smart_plug_with_energy'; // hub → fall back to known device, admin will refine
    case 'homewizard_local': return 'homewizard_p1_direct';
    default: return 'unknown_device';
  }
}

function Result({ r, route, onAdd, added }) {
  const sev = r.confidence < 0.5 ? 'bg-red-50 border-red-200'
    : r.confidence < 0.85 ? 'bg-amber-50 border-amber-200' : 'bg-white border-border';
  return (
    <div className={`border rounded-lg p-3 text-xs ${sev}`} data-testid={`onb-result-${r.device_key}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold">{r.brand || '—'} {r.model || ''}</div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
          {Math.round((r.confidence || 0) * 100)}%
        </span>
      </div>
      <div className="text-[11px] text-muted-foreground">{r.device_key}</div>
      <div className="mt-2 flex gap-2">
        {added ? (
          <span className="text-green-700 flex items-center gap-1" data-testid={`onb-added-${r.device_key}`}>
            <CheckCircle2 className="w-3 h-3" /> Toegevoegd aan Powersure
          </span>
        ) : (
          <button onClick={() => onAdd(r, route)}
            data-testid={`onb-add-${r.device_key}`}
            className="px-3 py-1 bg-primary text-white rounded text-xs flex items-center gap-1">
            <Plus className="w-3 h-3" /> Toevoegen
          </button>
        )}
      </div>
    </div>
  );
}

// Status: idle | analyzing | recognized | pending_review | error_unsupported | error_too_large | error_vision_unavailable | error_other
function PhotoRealResult({ result, status, errorMsg, onAdd, added, onReset }) {
  if (status === 'idle') return null;

  if (status === 'analyzing') {
    return (
      <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 text-xs flex items-center gap-2"
        data-testid="onb-photo-real-analyzing">
        <Loader2 className="w-4 h-4 animate-spin" />
        Gemini Vision analyseert foto... (geen bytes opgeslagen)
      </div>
    );
  }

  if (status.startsWith('error_')) {
    const tone = 'border-red-200 bg-red-50 text-red-800';
    const titleMap = {
      error_unsupported: 'Bestandstype niet ondersteund',
      error_too_large: 'Bestand te groot',
      error_vision_unavailable: 'Gemini Vision niet beschikbaar',
      error_other: 'Herkenning mislukt',
    };
    return (
      <div className={`border rounded-lg p-3 text-xs flex items-start gap-2 ${tone}`}
        data-testid={`onb-photo-real-${status}`}>
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">{titleMap[status] || 'Fout'}</div>
          <div className="mt-0.5">{errorMsg}</div>
        </div>
        <button onClick={onReset} className="text-red-700" data-testid="onb-photo-real-reset">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // recognized / pending_review
  const r = result || {};
  const ve = r.vision_extracted || {};
  const isPending = status === 'pending_review';
  const tone = isPending
    ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50';

  return (
    <div className={`border rounded-lg p-3 text-xs space-y-2 ${tone}`}
      data-testid={`onb-photo-real-${status}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">
          {r.brand || 'Onbekend merk'} {r.model || ''}
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200"
          data-testid="onb-photo-real-confidence">
          {Math.round((r.confidence || 0) * 100)}% zekerheid
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        <div><span className="text-muted-foreground">Merk:</span> <span data-testid="onb-pr-brand">{r.brand || '—'}</span></div>
        <div><span className="text-muted-foreground">Model:</span> <span data-testid="onb-pr-model">{r.model || '—'}</span></div>
        <div><span className="text-muted-foreground">Type:</span> <span data-testid="onb-pr-device-type">{ve.device_type || '—'}</span></div>
        <div><span className="text-muted-foreground">Categorie:</span> <span data-testid="onb-pr-category">{ve.category_hint || '—'}</span></div>
        <div className="col-span-2"><span className="text-muted-foreground">Capabilities:</span>{' '}
          <span data-testid="onb-pr-capabilities">{(ve.possible_capabilities || []).join(', ') || '—'}</span></div>
        <div><span className="text-muted-foreground">Device key:</span> <code className="text-[10px]">{r.device_key}</code></div>
      </div>

      {isPending && (
        <div className="bg-amber-100 border border-amber-300 rounded p-2 text-amber-900"
          data-testid="onb-photo-real-pending-banner">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          <strong>Pending review.</strong> Lage zekerheid of onbekend apparaat — controleer handmatig vóór toevoegen.
        </div>
      )}

      <div className="bg-white/60 border border-slate-200 rounded p-2 text-[11px] text-slate-700 flex items-start gap-1.5"
        data-testid="onb-photo-real-pii-notice">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-green-700 flex-shrink-0" />
        <span>
          <strong>PII gestript:</strong> serials, barcodes, QR-codes, MAC- en IP-adressen
          worden niet opgeslagen. Originele foto blijft niet bewaard.
        </span>
      </div>

      <div className="flex gap-2 pt-1">
        {added ? (
          <span className="text-green-700 flex items-center gap-1 text-xs"
            data-testid="onb-photo-real-added">
            <CheckCircle2 className="w-4 h-4" /> Toegevoegd aan Powersure
          </span>
        ) : (
          <button onClick={() => onAdd(r, 'photo_recognition')}
            data-testid="onb-photo-real-add"
            className="px-3 py-1.5 bg-primary text-white rounded text-xs flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Toevoegen aan Powersure
          </button>
        )}
        <button onClick={onReset} className="px-3 py-1.5 bg-white border border-border rounded text-xs"
          data-testid="onb-photo-real-clear">
          Nieuwe foto
        </button>
      </div>
    </div>
  );
}

export default function DeviceOnboardingTab({ token }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [network, setNetwork] = useState([]);
  const [homey, setHomey] = useState([]);
  const [photoMock, setPhotoMock] = useState(null);
  const [photoToken, setPhotoToken] = useState('airco_typeplaatje');
  const [manualQuery, setManualQuery] = useState('');
  const [manualResults, setManualResults] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [addedKeys, setAddedKeys] = useState(new Set());
  const [helperCandidates, setHelperCandidates] = useState([]);

  // ---- Real photo upload state ----
  const [photoPreview, setPhotoPreview] = useState(null);     // data: URL for <img>
  const [photoFile, setPhotoFile] = useState(null);
  const [photoStatus, setPhotoStatus] = useState('idle');     // idle/analyzing/recognized/pending_review/error_*
  const [photoResult, setPhotoResult] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [photoAdded, setPhotoAdded] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = useCallback(async (signal) => {
    const [n, h, p, nh] = await Promise.all([
      axios.get(`${API}/admin/device-onboarding/scan/network`, { ...H, signal }),
      axios.get(`${API}/admin/device-onboarding/scan/homey`, { ...H, signal }),
      axios.get(`${API}/admin/device-onboarding/profiles`, { ...H, signal }),
      axios.get(`${API}/admin/device-onboarding/network/recent`, { ...H, signal }),
    ]);
    return {
      network: n.data?.results || [],
      homey: h.data?.results || [],
      profiles: p.data?.items || [],
      addedKeys: new Set((p.data?.items || []).map((it) => it.brand + '|' + it.model)),
      helperCandidates: nh.data?.items || [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const ctl = new AbortController();
    load(ctl.signal)
      .then((data) => {
        if (ctl.signal.aborted) return;
        setNetwork(data.network);
        setHomey(data.homey);
        setProfiles(data.profiles);
        setAddedKeys(data.addedKeys);
        setHelperCandidates(data.helperCandidates);
      })
      .catch((e) => {
        if (ctl.signal.aborted || e?.name === 'CanceledError') return;
        toast.error(e?.response?.data?.detail || e.message);
      });
    return () => ctl.abort();
  }, [load]);

  const reload = useCallback(async () => {
    try {
      const data = await load();
      setNetwork(data.network);
      setHomey(data.homey);
      setProfiles(data.profiles);
      setAddedKeys(data.addedKeys);
      setHelperCandidates(data.helperCandidates);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }, [load]);

  const runPhotoMock = async () => {
    try {
      const r = await axios.get(`${API}/admin/device-onboarding/scan/photo`,
        { ...H, params: { photo_token: photoToken } });
      setPhotoMock(r.data.result);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const runManual = async () => {
    try {
      const r = await axios.get(`${API}/admin/device-onboarding/scan/manual`,
        { ...H, params: { q: manualQuery } });
      setManualResults(r.data?.results || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const addDevice = async (r, route) => {
    try {
      await axios.post(`${API}/admin/device-onboarding/add`,
        { device_key: r.device_key, route, confidence: r.confidence,
          brand: r.brand, model: r.model }, H);
      toast.success(`${r.brand || ''} ${r.model || r.device_key} toegevoegd`);
      setAddedKeys(new Set([...addedKeys, r.device_key]));
      if (route === 'photo_recognition' && photoResult && r.device_key === photoResult.device_key) {
        setPhotoAdded(true);
      }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  // ---- Real photo handlers ----
  const resetPhoto = () => {
    setPhotoStatus('idle');
    setPhotoResult(null);
    setPhotoError('');
    setPhotoPreview(null);
    setPhotoFile(null);
    setPhotoAdded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handlePhotoFile = (file) => {
    if (!file) return;
    setPhotoResult(null);
    setPhotoAdded(false);

    if (!ALLOWED_MIME.includes(file.type)) {
      setPhotoStatus('error_unsupported');
      setPhotoError(`Type '${file.type || 'onbekend'}' wordt niet ondersteund. Gebruik JPEG, PNG of WebP.`);
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoStatus('error_too_large');
      const mb = (file.size / 1024 / 1024).toFixed(2);
      setPhotoError(`Bestand is ${mb} MB, maximaal 8 MB toegestaan. Kies een kleinere foto.`);
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result || null);
    reader.readAsDataURL(file);
    uploadPhoto(file);
  };

  const uploadPhoto = async (file) => {
    setPhotoStatus('analyzing');
    setPhotoError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/admin/device-onboarding/scan/photo-real`,
        fd,
        { headers: { ...H.headers, 'Content-Type': 'multipart/form-data' },
          timeout: 60000 },
      );
      const result = r.data?.result || {};
      const conf = Number(result.confidence || 0);
      const isUnknown = result.device_key === 'unknown_device';
      const isPending = conf < 0.5 || isUnknown
        || r.data?.control_summary?.vision_status === 'error';
      setPhotoResult(result);
      setPhotoStatus(isPending ? 'pending_review' : 'recognized');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400 && detail?.error === 'unsupported_mime') {
        setPhotoStatus('error_unsupported');
        setPhotoError(`Server weigerde MIME ${detail.received || ''}. Gebruik JPEG/PNG/WebP.`);
      } else if (status === 400 && detail?.error === 'file_too_large') {
        setPhotoStatus('error_too_large');
        setPhotoError(`Server limiet: max ${Math.round((detail.max_bytes || 0) / 1024 / 1024)} MB.`);
      } else if (status === 400 && detail?.error === 'validation_failed') {
        setPhotoStatus('error_unsupported');
        setPhotoError(detail.reason || 'Validatie mislukt.');
      } else if (status === 503) {
        setPhotoStatus('error_vision_unavailable');
        setPhotoError('Gemini Vision is tijdelijk niet bereikbaar. Probeer opnieuw of voeg handmatig toe.');
      } else if (status === 401 || status === 403) {
        setPhotoStatus('error_other');
        setPhotoError('Niet geautoriseerd. Log opnieuw in.');
      } else {
        setPhotoStatus('error_other');
        setPhotoError(err?.message || 'Onbekende fout bij foto-upload.');
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs">
        <strong>Device Onboarding v2.</strong> Echte foto-herkenning via Gemini Vision is live.
        Netwerk-scan en Homey-discovery zijn nog mock. Geen live control, geen IP/MAC/PII opslag.
      </div>

      {/* Helper-submitted candidates (real LAN discovery via local helper) */}
      <div className="border border-emerald-300 rounded-xl p-3 bg-white"
        data-testid="onb-helper-submissions">
        <div className="font-semibold flex items-center gap-2 mb-2">
          <Wifi className="w-4 h-4 text-emerald-700" />
          1b. Lokale netwerk-discovery via Powersure Helper
          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">v2 · real</span>
        </div>
        <div className="text-[11px] text-muted-foreground mb-2">
          Veilige kandidaten van de Local Helper. <strong>IP/MAC/hostname blijven lokaal</strong>,
          ze worden niet meegestuurd en niet bewaard.
        </div>
        {helperCandidates.length === 0 ? (
          <div className="text-[11px] text-muted-foreground italic">
            Geen submissions. Start de Local Helper, klik &quot;Zoek apparaten op lokaal netwerk&quot; en
            &quot;Stuur veilige kandidaten naar Powersure&quot;.
          </div>
        ) : (
          <div className="space-y-2">
            {helperCandidates.slice(0, 5).map((rec) => (
              <div key={rec._id} className="border border-border rounded p-2"
                data-testid={`onb-helper-sub-${rec._id}`}>
                <div className="text-[11px] flex justify-between mb-1">
                  <span className="font-mono text-muted-foreground">{rec.ts?.slice(0, 19)}</span>
                  <span>helper v{rec.helper_version || '?'} · {rec.candidates_count} kandidaten</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {(rec.candidates || []).map((c, i) => (
                    <Result key={i}
                      r={{ device_key: c.source_type, brand: c.brand,
                           model: c.model, confidence: c.confidence }}
                      route="network_scan_helper"
                      onAdd={() => addDevice(
                        { device_key: _mapHelperSourceToFakeKey(c.source_type),
                          brand: c.brand, model: c.model, confidence: c.confidence },
                        'network_scan',
                      )}
                      added={false} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Network */}
      <div className="border border-border rounded-xl p-3 bg-white">
        <div className="font-semibold flex items-center gap-2 mb-2">
          <Wifi className="w-4 h-4" /> 1. Zoek apparaten op mijn netwerk
          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">mock</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {network.map((r) => <Result key={r.device_key} r={r} route="network_scan"
            onAdd={addDevice} added={addedKeys.has(r.device_key)} />)}
        </div>
      </div>

      {/* Homey */}
      <div className="border border-border rounded-xl p-3 bg-white">
        <div className="font-semibold flex items-center gap-2 mb-2">
          <Search className="w-4 h-4" /> 2. Homey/HomeWizard discovery
          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">mock</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {homey.map((r) => <Result key={r.device_key} r={r} route="homey_homewizard_discovery"
            onAdd={addDevice} added={addedKeys.has(r.device_key)} />)}
        </div>
      </div>

      {/* Photo — REAL (Gemini Vision) */}
      <div className="border border-emerald-300 rounded-xl p-3 bg-white" data-testid={PHOTO_REAL_TESTID}>
        <div className="font-semibold flex items-center gap-2 mb-2">
          <Camera className="w-4 h-4" /> 3. Maak of upload foto van apparaat
          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">Gemini Vision</span>
        </div>

        <div className="text-[11px] text-muted-foreground mb-2">
          JPEG · PNG · WebP · max 8 MB. Foto blijft in-memory en wordt niet opgeslagen.
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => fileInputRef.current?.click()}
            data-testid="onb-photo-real-upload-btn"
            disabled={photoStatus === 'analyzing'}
            className="px-3 py-1.5 bg-primary text-white rounded text-xs flex items-center gap-1 disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" /> Foto kiezen
          </button>
          <button onClick={() => cameraInputRef.current?.click()}
            data-testid="onb-photo-real-camera-btn"
            disabled={photoStatus === 'analyzing'}
            className="px-3 py-1.5 bg-white border border-border rounded text-xs flex items-center gap-1 disabled:opacity-50">
            <Camera className="w-3.5 h-3.5" /> Maak foto
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
            data-testid="onb-photo-real-file-input"
            className="hidden"
            onChange={(e) => handlePhotoFile(e.target.files?.[0])} />
          <input ref={cameraInputRef} type="file" accept="image/jpeg,image/png,image/webp"
            capture="environment"
            data-testid="onb-photo-real-camera-input"
            className="hidden"
            onChange={(e) => handlePhotoFile(e.target.files?.[0])} />
        </div>

        {photoPreview && (
          <div className="mb-2">
            <img src={photoPreview} alt="preview"
              data-testid="onb-photo-real-preview"
              className="max-h-32 rounded border border-border" />
          </div>
        )}

        <PhotoRealResult
          result={photoResult} status={photoStatus} errorMsg={photoError}
          onAdd={addDevice} added={photoAdded} onReset={resetPhoto} />
      </div>

      {/* Photo — MOCK (kept for testing convenience) */}
      <details className="border border-border rounded-xl p-3 bg-slate-50">
        <summary className="font-semibold flex items-center gap-2 cursor-pointer text-xs">
          <Camera className="w-3.5 h-3.5" /> 3b. Mock-foto (vooraf-gedefinieerde scenarios)
        </summary>
        <div className="mt-2 flex gap-2 items-center">
          <select value={photoToken} onChange={(e) => setPhotoToken(e.target.value)}
            className="border border-border rounded px-2 py-1 text-xs"
            data-testid="onb-photo-token">
            <option value="airco_typeplaatje">Airco typeplaatje</option>
            <option value="inverter_typeplaatje">Inverter typeplaatje</option>
            <option value="mystery_device_typeplaatje">Onbekend apparaat</option>
          </select>
          <button onClick={runPhotoMock} className="px-3 py-1 bg-slate-700 text-white rounded text-xs"
            data-testid="onb-photo-run">Herken (mock)</button>
        </div>
        {photoMock && <div className="mt-2"><Result r={photoMock} route="photo_recognition"
          onAdd={addDevice} added={addedKeys.has(photoMock.device_key)} /></div>}
      </details>

      {/* Manual */}
      <div className="border border-border rounded-xl p-3 bg-white">
        <div className="font-semibold flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4" /> 4. Handmatig zoeken
        </div>
        <div className="flex gap-2 mb-2">
          <input value={manualQuery} onChange={(e) => setManualQuery(e.target.value)}
            placeholder="b.v. daikin, p1, sonnen"
            className="flex-1 border border-border rounded px-2 py-1 text-xs"
            data-testid="onb-manual-query" />
          <button onClick={runManual} className="px-3 py-1 bg-primary text-white rounded text-xs"
            data-testid="onb-manual-run">Zoek</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {manualResults.map((r) => <Result key={r.device_key} r={r} route="manual_catalog"
            onAdd={addDevice} added={addedKeys.has(r.device_key)} />)}
        </div>
      </div>

      {/* Profiles */}
      <div className="border border-border rounded-xl p-3 bg-white">
        <div className="font-semibold mb-2">Toegevoegde safe device profiles ({profiles.length})</div>
        <div className="text-[11px] grid gap-1 max-h-64 overflow-auto">
          {profiles.map((p) => (
            <div key={p.profile_id} className="grid grid-cols-6 gap-2 border-b border-border/40 py-1"
              data-testid={`onb-profile-${p.profile_id}`}>
              <div className="truncate">{p.brand} {p.model}</div>
              <div className="font-mono">{p.category}</div>
              <div className="font-mono">{p.source_type}</div>
              <div className="font-mono">{p.duplicate_status}</div>
              <div className={p.control_status === 'control_blocked' ? 'text-red-700' : ''}>{p.control_status}</div>
              <div>{Math.round((p.confidence || 0) * 100)}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
